import { useCallback, useRef } from "react";

/**
 * localStorage-backed draft persistence for the omnichannel composer.
 *
 * Provides:
 *  - saveDraft   — write a draft immediately (also used by the debounced auto-save)
 *  - clearDraft  — remove draft on send / discard
 *  - readDraft   — restore draft on conversation open
 *  - scheduleSave — debounced save (call on every keystroke; fires 2 s after last change)
 *  - draftSaveTimerRef — exposed so callers can cancel the pending timer if needed
 *
 * Key:   `draft_<conversationId>` in localStorage
 */
export function useDraftPersistence() {
  const draftSaveTimerRef = useRef(null);

  const saveDraft = useCallback((conversationId, text) => {
    if (!conversationId || !text) return;
    try {
      localStorage.setItem(`draft_${conversationId}`, text);
    } catch (_) { /* storage unavailable — silently ignore */ }
  }, []);

  const clearDraft = useCallback((conversationId) => {
    if (!conversationId) return;
    try {
      localStorage.removeItem(`draft_${conversationId}`);
    } catch (_) { /* ignore */ }
  }, []);

  const readDraft = useCallback((conversationId) => {
    if (!conversationId) return "";
    try {
      return localStorage.getItem(`draft_${conversationId}`) || "";
    } catch (_) {
      return "";
    }
  }, []);

  /**
   * Schedule a debounced save — replaces any pending timer.
   * Cancels automatically on unmount if the caller cleans up via the returned cancel fn.
   *
   * @param {string|number} conversationId
   * @param {string}        text
   * @param {number}        [delayMs=2000]
   * @returns {() => void} cancel function
   */
  const scheduleSave = useCallback((conversationId, text, delayMs = 2000) => {
    clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      if (conversationId && text) {
        try {
          localStorage.setItem(`draft_${conversationId}`, text);
        } catch (_) { /* ignore */ }
      }
    }, delayMs);
    return () => clearTimeout(draftSaveTimerRef.current);
  }, []);

  return { saveDraft, clearDraft, readDraft, scheduleSave, draftSaveTimerRef };
}
